import { useState } from 'react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { DebugTabs } from '@/components/debug/DebugTabs';
import { Button } from '@/components/ui/button';
import { useSession } from '@/lib/session';
import {
  useReviewQueue,
  useReviewAction,
  useWwdQuartz,
  useRunJob,
  useWwdUsage,
  type ReviewItemDto,
  type WwdQuartzJobDto,
} from '../lib/api';

// WWD Debug — admin-only (WwdApp adds the tab only for portal admins; the BFF proxy adds
// X-Portal-Role: admin so the WWD Api's /api/admin gate passes). Three sections, in the
// HMHWatch Debug/Usage idioms: the human review queue, the Quartz schedule (with manual
// triggers), and the provider usage rollup.

type TabId = 'review' | 'quartz' | 'usage';

export default function Debug() {
  const { data: session } = useSession();
  const isAdmin = !!session?.isAdmin;
  const [tab, setTab] = useState<TabId>('review');

  const review = useReviewQueue(isAdmin);
  const quartz = useWwdQuartz(isAdmin);

  if (!isAdmin) {
    return (
      <Card>
        <CardTitle>Debug</CardTitle>
        <CardDescription className="mt-2">The WWD debug console is admin-only.</CardDescription>
      </Card>
    );
  }

  const tabs = [
    { id: 'review', label: 'Review Queue', count: review.data?.items.length ?? 0 },
    { id: 'quartz', label: 'Quartz Schedule', count: quartz.data?.length ?? 0 },
    { id: 'usage', label: 'Usage' },
  ];

  return (
    <>
      <Card>
        <CardTitle>Debug</CardTitle>
        <CardDescription className="mt-2">
          Human review queue, Quartz schedule with manual triggers, and provider usage.{' '}
          <span className="text-accent">Admin-only</span>
        </CardDescription>
      </Card>

      <DebugTabs tabs={tabs} value={tab} onChange={(id) => setTab(id as TabId)} />

      {tab === 'review' && (
        review.isLoading ? <LoadingSkeleton lines={6} /> :
        review.error ? <ErrorState message={(review.error as Error).message} /> :
        <ReviewQueue items={review.data?.items ?? []} />
      )}

      {tab === 'quartz' && (
        quartz.isLoading ? <LoadingSkeleton lines={6} /> :
        quartz.error ? <ErrorState message={(quartz.error as Error).message} /> :
        <QuartzSection jobs={quartz.data ?? []} />
      )}

      {tab === 'usage' && <UsageSection />}
    </>
  );
}

/* ---------- Review queue ---------- */

function ReviewQueue({ items }: { items: ReviewItemDto[] }) {
  const action = useReviewAction();

  return (
    <Card>
      <h2 className="text-xl font-semibold mb-3">
        Review Queue{' '}
        <span className="text-muted text-sm font-normal">
          (extractions flagged needs_review — approve to publish, reject to discard)
        </span>
      </h2>
      {items.length === 0 ? (
        <p className="text-muted">Nothing awaiting review.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-accent">
                <Th>Table</Th>
                <Th>Id</Th>
                <Th>Summary</Th>
                <Th>Reason</Th>
                <Th>Flagged</Th>
                <Th right>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-border align-top hover:bg-surface-hover">
                  <td className="py-3 px-3 whitespace-nowrap text-muted">{it.kind}</td>
                  <td className="py-3 px-3 whitespace-nowrap font-mono text-xs text-muted">{it.id}</td>
                  <td className="py-3 px-3 max-w-96">
                    {it.url ? (
                      <a href={it.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                        {it.summary}
                      </a>
                    ) : (
                      it.summary
                    )}
                  </td>
                  <td className="py-3 px-3 max-w-72 text-muted">{it.reviewNote ?? '—'}</td>
                  <td className="py-3 px-3 whitespace-nowrap text-muted">{fmtDate(it.flaggedAt)}</td>
                  <td className="py-3 px-3 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={action.isPending}
                      onClick={() => action.mutate({ id: it.id, action: 'approve' })}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      className="ml-2"
                      disabled={action.isPending}
                      onClick={() => action.mutate({ id: it.id, action: 'reject' })}
                    >
                      Reject
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {action.error && <p className="mt-3 text-sm text-accent-danger">{(action.error as Error).message}</p>}
    </Card>
  );
}

/* ---------- Quartz schedule ---------- */

function QuartzSection({ jobs }: { jobs: WwdQuartzJobDto[] }) {
  const run = useRunJob();
  const [lastResult, setLastResult] = useState<string | null>(null);

  return (
    <Card>
      <h2 className="text-xl font-semibold mb-3">
        Quartz Schedule <span className="text-muted text-sm font-normal">(auto-refresh 8s)</span>
      </h2>
      {jobs.length === 0 ? (
        <p className="text-muted">Worker unreachable or no jobs registered.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-accent">
              <Th>Job</Th>
              <Th>Cron</Th>
              <Th>Last fire</Th>
              <Th>Next fire</Th>
              <Th right>Run</Th>
            </tr>
          </thead>
          <tbody>
            {[...jobs].sort((a, b) => a.key.localeCompare(b.key)).map((j) => (
              <tr key={j.key} className="border-b border-border hover:bg-surface-hover">
                <td className="py-3 px-3 font-medium">{j.key}</td>
                <td className="py-3 px-3 text-muted font-mono text-xs">{j.cron ?? '—'}</td>
                <td className="py-3 px-3 text-muted">{fmtTs(j.previousFire)}</td>
                <td className="py-3 px-3 text-muted">{fmtTs(j.nextFire)}</td>
                <td className="py-3 px-3 text-right">
                  <Button
                    size="sm"
                    disabled={run.isPending}
                    onClick={() =>
                      run.mutate(j.key, {
                        onSuccess: (r) => setLastResult(r.message),
                        onError: (e) => setLastResult((e as Error).message),
                      })
                    }
                  >
                    ▶ Run
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {lastResult && <p className="mt-3 text-sm text-muted">{lastResult}</p>}
    </Card>
  );
}

/* ---------- Usage ---------- */

const selectClass =
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11 ' +
  'font-(family-name:--font-body) focus:outline-2 focus:outline-offset-2 focus:outline-accent';

function UsageSection() {
  const [month, setMonth] = useState(currentMonth);
  const { data, isLoading, error } = useWwdUsage(month, true);
  const months = lastNMonths(12);

  return (
    <>
      <Card>
        <h2 className="text-xl font-semibold">API Usage</h2>
        <p className="text-muted text-sm mt-1">Monthly provider spend and call volume.</p>
        <label className="flex items-center gap-2 mt-4">
          <span className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">Month</span>
          <select className={selectClass} value={month} onChange={(e) => setMonth(e.target.value)}>
            {months.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
      </Card>

      {isLoading && <LoadingSkeleton lines={4} />}
      {error && <ErrorState message={(error as Error).message} />}

      {data && (
        <Card>
          <div className="flex flex-wrap gap-x-8 gap-y-3 mb-4">
            <Stat label="Total cost" value={fmtUsd.format(data.totalCostUsd)} />
            <Stat label="API calls" value={fmtNum.format(data.totalCalls)} />
            <Stat label="Providers" value={String(data.providers.length)} />
          </div>
          {data.providers.length === 0 ? (
            <p className="text-muted text-sm">No API usage recorded for {month}.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-accent">
                  <Th>Provider</Th>
                  <Th right>Calls</Th>
                  <Th right>Cost (USD)</Th>
                  <Th right>Tokens in</Th>
                  <Th right>Tokens out</Th>
                </tr>
              </thead>
              <tbody>
                {data.providers.map((r) => (
                  <tr key={r.provider} className="border-b border-border hover:bg-surface-hover">
                    <td className="py-3 px-3 font-medium">{r.provider}</td>
                    <td className="py-3 px-3 text-right tabular-nums">{fmtNum.format(r.calls)}</td>
                    <td className="py-3 px-3 text-right tabular-nums">{fmtUsd.format(r.costUsd)}</td>
                    <td className="py-3 px-3 text-right tabular-nums">{fmtNum.format(r.tokensIn)}</td>
                    <td className="py-3 px-3 text-right tabular-nums">{fmtNum.format(r.tokensOut)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </>
  );
}

/* ---------- shared bits ---------- */

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui) ${right ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">{label}</div>
      <div className="text-2xl font-(family-name:--font-heading) font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

const fmtUsd = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const fmtNum = new Intl.NumberFormat();

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 16).replace('T', ' ');
}

function fmtTs(iso: string | null): string {
  if (!iso) return '—';
  const delta = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(delta);
  const dir = delta < 0 ? 'ago' : 'in';
  const m = Math.round(abs / 60_000);
  if (m < 60) return `${m}m ${dir}`;
  const h = Math.round(abs / 3_600_000);
  if (h < 48) return `${h}h ${dir}`;
  const d = Math.round(abs / 86_400_000);
  return `${d}d ${dir}`;
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function lastNMonths(n: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}
